import {action, computed, makeObservable, observable} from 'mobx';
import s from '../s';
import {docSizeTilesShown, selectDocSize} from '../selection/doc-size';
import {setGradYearInput} from '../selection/grad-year';
import {buildSizeQuestions, groupByKey, isAnsweredYear, openQuestions, rowForAnswers, yearShown, type SizeQuestions} from './size-questions';

export type SizeQuestionId = 'degree' | 'year';

/**
 * The redesign's own state, none of which is product state: the size answers (the degree group, the year as typed), whether Add To
 * Cart was pressed while a question was open (the missed state), and the "See all" toggles. Product state (s.ps) changes only
 * through the shared selection handlers, so the same picks post the same /api/atc body in both layouts.
 */
class RedesignStore {
    /** The answered degree group (DocSizeName), or null. */
    @observable degree: string | null = null;
    /** The year field's digits. */
    @observable year = '';
    /** Add To Cart was pressed while a size question was open. */
    @observable tried = false;
    /** Add To Cart was pressed without the graduation year a "pre-" product requires. */
    @observable gradYearMissed = false;
    @observable framesOpen = false;
    @observable matsOpen = false;
    @observable sealsOpen = false;

    constructor() {
        makeObservable(this);
    }

    /** The current school's size questions (none without automated sizes, or with fewer than two size rows). */
    @computed get questions(): SizeQuestions {
        return buildSizeQuestions(docSizeTilesShown() ? s.docsizes_available : []);
    }

    @computed get degreeShown(): boolean {
        return this.questions.degreeNeeded;
    }

    @computed get yearShown(): boolean {
        return this.questions.groups.length > 0 && yearShown(this.questions, this.degree);
    }

    @computed get open(): SizeQuestionId[] {
        return openQuestions(this.questions, {degree: this.degree, year: this.year});
    }

    @computed get yearAnswered(): boolean {
        return isAnsweredYear(this.year);
    }

    @action
    reset = (): void => {
        this.degree = null;
        this.year = s.ui.grad_year_input || '';
        this.tried = false;
        this.gradYearMissed = false;
        this.framesOpen = false;
        this.matsOpen = false;
        this.sealsOpen = false;
    };

    /** "See all" / "See fewer" on the frame, mat and seal tiles. */
    @action
    toggle = (key: 'framesOpen' | 'matsOpen' | 'sealsOpen'): void => {
        this[key] = !this[key];
    };

    /** Add To Cart was pressed: open questions show their missed state from now on. */
    @action
    markTried = (): void => {
        this.tried = true;
    };

    @action
    setGradYearMissed = (missed: boolean): void => {
        this.gradYearMissed = missed;
    };

    @action
    answerDegree = (key: string): void => {
        this.degree = key;
        this.applySize();
    };

    /**
     * The graduation year, typed in the size question (setsSize) or in the school step's optional field. It becomes the order's
     * GradYear as the control's field does (setGradYearInput), on the products that show that field; only the size question
     * changes the size.
     */
    @action
    answerYear = (value: string, setsSize: boolean): string => {
        const digits = value.replace(/\D/g, '').slice(0, 4);
        this.year = digits;
        if (s.ui.show_grad_year) {
            setGradYearInput(digits);
        }
        if (isAnsweredYear(digits)) {
            this.gradYearMissed = false;
        }
        if (setsSize) {
            this.applySize();
        }
        return digits;
    };

    /** After a school change (selectLino re-runs the sizes): a degree the new school does not group its sizes by is open again. */
    @action
    afterSchoolChange = (): void => {
        if (this.degree != null && !groupByKey(this.questions, this.degree)) {
            this.degree = null;
        }
        this.applySize();
    };

    /** Sets the size the answers point to (the shared doc size handler), once an answer exists. */
    @action
    applySize = (): void => {
        if (this.questions.groups.length === 0 || (this.degree == null && !isAnsweredYear(this.year))) {
            return;
        }
        const row = rowForAnswers(this.questions, {degree: this.degree, year: this.year}, s.docsizes_selected_id, Date.now());
        if (row && row.Id !== s.docsizes_selected_id) {
            selectDocSize(row.Id);
        }
    };
}

export const redesign = new RedesignStore();
